import { TableComponent as Table } from '../../components'
import { Input, Button, Tag, Space, Tooltip } from 'antd'
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons'
import moment from 'moment'
import { maskCurrency } from '../../utils/functions'

const SaquesTable = (props) => {
    const { 
        data, 
        loading, 
        showDetailsModal, 
        handleApprove, 
        handleReject, 
        handleFinalize, 
        refreshList,
        isHistoryMode 
    } = props
    
    // Status tags with appropriate colors
    const renderStatus = (status) => {
        // Status numérico: 0=Pendente, 1=Aprovado, 2=Rejeitado, 3=Finalizado
        if (status === 0 || status === 'pending') {
            return <Tag color="orange">Pendente</Tag>
        } else if (status === 1 || status === 'approved') {
            return <Tag color="blue">Aprovado</Tag>
        } else if (status === 2 || status === 4 || status === 'rejected') {
            return <Tag color="red">Rejeitado</Tag>
        } else if (status === 3 || status === 'finalized') {
            return <Tag color="green">Finalizado</Tag>
        }
        return <Tag>{status}</Tag>
    }
    
    // Payment method tags
    const renderPaymentMethod = (method) => {
        if (method === 'pix') {
            return <Tag color="purple">PIX</Tag>
        } else if (method === 'bank') {
            return <Tag color="cyan">Dados Bancários</Tag>
        }
        return <Tag>{method}</Tag>
    }
    
    // Create column definitions for the table
    const getColumns = () => {
        let columns = [
            {
                title: 'ID',
                dataIndex: 'id',
                key: 'id',
                align: 'center',
                width: '5%',
                sorter: {
                    compare: (a, b) => a.id - b.id,
                    multiple: 1
                }
            },
            {
                title: 'Usuário',
                dataIndex: 'usuario',
                key: 'usuario',
                align: 'center',
                width: '15%',
                render: usuario => usuario?.nome || '-',
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            placeholder="Buscar usuário"
                            value={selectedKeys[0]}
                            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={() => confirm()}
                            style={{ width: 188, marginBottom: 8, display: 'block' }}
                        />
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => confirm()}
                                icon={<SearchOutlined />}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Buscar
                            </Button>
                            <Button
                                onClick={() => clearFilters()}
                                size="small"
                                style={{ width: 90 }}
                            >
                                Limpar
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
                onFilter: (value, record) => record.usuario?.nome?.toLowerCase().includes(value.toLowerCase()),
            },
            {
                title: 'Forma de Pagamento',
                dataIndex: 'paymentMethod',
                key: 'paymentMethod',
                align: 'center',
                width: '10%',
                render: renderPaymentMethod,
                filters: [
                    { text: 'PIX', value: 'pix' },
                    { text: 'Dados Bancários', value: 'bank' }
                ],
                onFilter: (value, record) => record.paymentMethod === value,
            },
            {
                title: 'Valor',
                dataIndex: 'valor',
                key: 'valor',
                align: 'center',
                width: '10%',
                render: value => `R$ ${maskCurrency(value)}`,
                sorter: {
                    compare: (a, b) => {
                        // Handle sorting for both string and number types
                        const aValue = typeof a.valor === 'string' ? parseFloat(a.valor) : a.valor;
                        const bValue = typeof b.valor === 'string' ? parseFloat(b.valor) : b.valor;
                        return aValue - bValue;
                    },
                    multiple: 2
                },
            },
            {
                title: 'Saldo Disponível',
                dataIndex: 'saldoDisponivel',
                key: 'saldoDisponivel',
                align: 'center',
                width: '10%',
                render: value => `R$ ${maskCurrency(value)}`,
                sorter: {
                    compare: (a, b) => {
                        const aValue = a.saldoDisponivel || 0;
                        const bValue = b.saldoDisponivel || 0;
                        return aValue - bValue;
                    },
                    multiple: 3
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                align: 'center',
                width: '10%',
                render: renderStatus,
                filters: [
                    { text: 'Pendente', value: 0 },
                    { text: 'Aprovado', value: 1 },
                    { text: 'Rejeitado', value: [2, 4] },
                    { text: 'Finalizado', value: 3 }
                ],
                onFilter: (value, record) => {
                    // Para rejeitado, tratamos os códigos 2 e 4
                    if (Array.isArray(value) && value.includes(2) && value.includes(4)) {
                        return record.status === 2 || record.status === 4 || record.status === 'rejected';
                    }
                    
                    // Converte para número para comparação
                    const statusValue = parseInt(value);
                    return record.status === statusValue || 
                           (statusValue === 0 && record.status === 'pending') ||
                           (statusValue === 1 && record.status === 'approved') ||
                           (statusValue === 2 && record.status === 'rejected') ||
                           (statusValue === 4 && record.status === 'rejected') ||
                           (statusValue === 3 && record.status === 'finalized');
                },
            },
            {
                title: 'Data de Solicitação',
                dataIndex: 'dataSolicitacao',
                key: 'dataSolicitacao',
                align: 'center',
                width: '10%',
                render: (date, record) => {
                    // Check multiple possible date fields
                    const dateToUse = date || record.dataPedido || record.createdAt || record.updatedAt;
                    return dateToUse ? moment(dateToUse).format('DD/MM/YYYY') : '-';
                },
                sorter: {
                    compare: (a, b) => {
                        const dateA = a.dataSolicitacao || a.dataPedido || a.createdAt || 0;
                        const dateB = b.dataSolicitacao || b.dataPedido || b.createdAt || 0;
                        return moment(dateA).unix() - moment(dateB).unix();
                    },
                    multiple: 4
                },
            }
        ];
        
        // Adicionar coluna de ações apenas se não estiver no modo histórico
        if (!isHistoryMode) {
            columns.push({
                title: 'Ações',
                key: 'actions',
                align: 'center',
                width: '15%',
                render: (_, record) => (
                    <Space size="small" style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Detalhes">
                            <Button 
                                type="primary" 
                                shape="circle" 
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showDetailsModal(record.id);
                                }}
                                className="btn-white-icon"
                                icon={
                                    <svg viewBox="64 64 896 896" focusable="false" data-icon="eye" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                        <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                    </svg>
                                }
                                style={{ backgroundColor: '#023047', borderColor: '#023047' }}
                            />
                        </Tooltip>
                        
                        {(record.status === 0 || record.status === 'pending') && record.podeAprovar && (
                            <>
                                <Tooltip title="Aprovar">
                                    <Button 
                                        type="primary" 
                                        shape="circle" 
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleApprove(record.id);
                                        }}
                                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                        className="btn-white-icon"
                                        icon={
                                            <svg viewBox="64 64 896 896" focusable="false" data-icon="check" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                                <path d="M912 190h-69.9c-9.8 0-19.1 4.5-25.1 12.2L404.7 724.5 207 474a32 32 0 00-25.1-12.2H112c-6.7 0-10.4 7.7-6.3 12.9l273.9 347c12.8 16.2 37.4 16.2 50.3 0l488.4-618.9c4.1-5.1.4-12.8-6.3-12.8z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                            </svg>
                                        }
                                    />
                                </Tooltip>
                                
                                <Tooltip title="Rejeitar">
                                    <Button 
                                        type="primary" 
                                        shape="circle" 
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleReject(record.id);
                                        }}
                                        style={{ backgroundColor: '#f5222d', borderColor: '#f5222d' }}
                                        className="btn-white-icon"
                                        icon={
                                            <svg viewBox="64 64 896 896" focusable="false" data-icon="close" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                                <path d="M563.8 512l262.5-312.9c4.4-5.2.7-13.1-6.1-13.1h-79.8c-4.7 0-9.2 2.1-12.3 5.7L511.6 449.8 295.1 191.7c-3-3.6-7.5-5.7-12.3-5.7H203c-6.8 0-10.5 7.9-6.1 13.1L459.4 512 196.9 824.9A7.95 7.95 0 00203 838h79.8c4.7 0 9.2-2.1 12.3-5.7l216.5-258.1 216.5 258.1c3 3.6 7.5 5.7 12.3 5.7h79.8c6.8 0 10.5-7.9 6.1-13.1L563.8 512z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                            </svg>
                                        }
                                    />
                                </Tooltip>
                            </>
                        )}
                        
                        {(record.status === 1 || record.status === 'approved') && (
                            <Tooltip title="Finalizar">
                                <Button 
                                    type="primary" 
                                    shape="circle" 
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleFinalize(record.id);
                                    }}
                                    style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                                    className="btn-white-icon"
                                    icon={
                                        <svg viewBox="64 64 896 896" focusable="false" data-icon="check-circle" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                            <path d="M699 353h-46.9c-10.2 0-19.9 4.9-25.9 13.3L469 584.3l-71.2-98.8c-6-8.3-15.6-13.3-25.9-13.3H325c-6.5 0-10.3 7.4-6.5 12.7l124.6 172.8a31.8 31.8 0 0051.7 0l210.6-292c3.9-5.3.1-12.7-6.4-12.7z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                            <path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                        </svg>
                                    }
                                />
                            </Tooltip>
                        )}
                    </Space>
                )
            });
        } else {
            // No modo histórico, adiciona apenas o botão de detalhes
            columns.push({
                title: 'Ações',
                key: 'actions',
                align: 'center',
                width: '10%',
                render: (_, record) => (
                    <Space size="small" style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Detalhes">
                            <Button 
                                type="primary" 
                                shape="circle" 
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    showDetailsModal(record.id);
                                }}
                                className="btn-white-icon"
                                icon={
                                    <svg viewBox="64 64 896 896" focusable="false" data-icon="eye" width="1em" height="1em" fill="#ffffff" aria-hidden="true" style={{fill: '#ffffff', pointerEvents: 'none'}}>
                                        <path d="M942.2 486.2C847.4 286.5 704.1 186 512 186c-192.2 0-335.4 100.5-430.2 300.3a60.3 60.3 0 000 51.5C176.6 737.5 319.9 838 512 838c192.2 0 335.4-100.5 430.2-300.3 7.7-16.2 7.7-35 0-51.5zM512 766c-161.3 0-279.4-81.8-362.7-254C232.6 339.8 350.7 258 512 258c161.3 0 279.4 81.8 362.7 254C791.5 684.2 673.4 766 512 766zm-4-430c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm0 288c-61.9 0-112-50.1-112-112s50.1-112 112-112 112 50.1 112 112-50.1 112-112 112z" fill="#ffffff" style={{fill: '#ffffff', pointerEvents: 'none'}} />
                                    </svg>
                                }
                                style={{ backgroundColor: '#023047', borderColor: '#023047' }}
                            />
                        </Tooltip>
                    </Space>
                )
            });
        }
        
        return columns;
    };
    
    // Component for table title with refresh button
    const TableTitle = () => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{isHistoryMode ? 'Histórico de Saques' : 'Solicitações de Saque'}</span>
            <Button
                icon={<ReloadOutlined />}
                onClick={refreshList}
                style={{ marginRight: 8 }}
            >
                Atualizar
            </Button>
        </div>
    );
    
    return (
        <Table
            column={getColumns()}
            data={data}
            loading={loading}
            titleTable={<TableTitle />}
            pagination={{ 
                showSizeChanger: true, 
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
                defaultPageSize: 10,
                pageSizeOptions: ['5', '10', '20', '50']
            }}
            rowKey="id"
        />
    );
}

export default SaquesTable; 